/**
 * Teams page — Ministry → Teams hierarchy
 * Level 1: select/create a Ministry
 * Level 2: manage teams within the selected Ministry
 * Follows web-design reference: dark theme, Playfair headings, sidebar nav
 */

import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import { createTeam } from '../../api/teams';
import { useTeamStore } from '../../stores/teamStore';
import { useMinistryStore } from '../../stores/ministryStore';
import { createMinistry, deleteMinistry, Ministry } from '../../api/ministries';
import TeamDetail from './components/TeamDetail';
import { apiErrorMessage } from '../../utils/apiError';

type View = 'ministry-list' | 'ministry-detail';

export default function TeamsPage() {
  const { teams, activeTeamId, setActiveTeam, loadTeams } = useTeamStore();
  const { ministries, activeMinistryId, setActiveMinistry, loadMinistries } = useMinistryStore();

  const [view, setView] = useState<View>('ministry-list');
  const [showCreateMinistry, setShowCreateMinistry] = useState(false);
  const [ministryName, setMinistryName] = useState('');
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMinistries();
    loadTeams();
  }, [loadMinistries, loadTeams]);

  const activeMinistry = ministries.find((m) => m.id === activeMinistryId) ?? null;
  const ministryTeams = teams.filter((t) => t.ministry_id === activeMinistryId);
  const activeTeam = teams.find((t) => t.id === activeTeamId && t.ministry_id === activeMinistryId) ?? null;

  const handleSelectMinistry = (m: Ministry) => {
    setActiveMinistry(m.id);
    setActiveTeam(null);
    setView('ministry-detail');
  };

  const handleCreateMinistry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ministryName.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const m = await createMinistry(ministryName.trim());
      setMinistryName('');
      setShowCreateMinistry(false);
      await loadMinistries();
      handleSelectMinistry(m);
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao criar ministério'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim() || !activeMinistryId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const newTeam = await createTeam(teamName.trim(), activeMinistryId);
      setTeamName('');
      setShowCreateTeam(false);
      await loadTeams();
      setActiveTeam(newTeam.id);
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao criar equipe'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMinistry = async (ministryId: string) => {
    if (!confirm('Deletar este ministério? Esta ação é irreversível.')) return;
    try {
      await deleteMinistry(ministryId);
      await loadMinistries();
      if (activeMinistryId === ministryId) {
        setActiveMinistry(null);
        setView('ministry-list');
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao deletar ministério'));
    }
  };

  // ── Ministry List View ─────────────────────────────────────────────────────
  if (view === 'ministry-list') {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-[#0A0A0F] text-white flex flex-col pt-16">
          {/* Header */}
          <div className="px-6 md:px-10 py-6 border-b border-white/5 bg-[#0D0C09]">
            <h1
              className="text-2xl font-semibold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ministérios
            </h1>
            <p className="text-white/30 text-sm mt-1">Selecione ou crie um ministério para gerenciar suas equipes</p>
          </div>

          <div className="flex-1 px-6 md:px-10 py-8">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm">
                {error}
              </div>
            )}

            {/* Create form */}
            {showCreateMinistry ? (
              <form
                onSubmit={handleCreateMinistry}
                className="mb-6 bg-white/[0.03] border border-white/10 rounded-2xl p-6 max-w-md"
              >
                <h3
                  className="text-white font-semibold mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Novo Ministério
                </h3>
                <input
                  type="text"
                  value={ministryName}
                  onChange={(e) => setMinistryName(e.target.value)}
                  placeholder="Nome do ministério"
                  autoFocus
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors mb-4"
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowCreateMinistry(false); setMinistryName(''); }}
                    className="flex-1 py-3 border border-white/10 text-white/60 hover:text-white rounded-xl text-sm transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !ministryName.trim()}
                    className="flex-1 py-3 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-xl text-sm hover:bg-[#D4B87A] disabled:opacity-50 transition-colors"
                  >
                    {isSubmitting ? 'Criando...' : 'Criar'}
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setShowCreateMinistry(true)}
                className="mb-6 flex items-center gap-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold px-5 py-3 rounded-xl text-sm hover:bg-[#D4B87A] transition-colors"
              >
                <i className="ri-add-line"></i>
                Novo Ministério
              </button>
            )}

            {/* Ministry cards */}
            {ministries.length === 0 ? (
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-12 text-center max-w-md">
                <i className="ri-building-line text-white/10 text-4xl mb-4 block"></i>
                <p className="text-white/30 text-sm">Nenhum ministério ainda</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
                {ministries.map((m) => {
                  const mTeams = teams.filter((t) => t.ministry_id === m.id);
                  return (
                    <div
                      key={m.id}
                      className="bg-white/[0.02] border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-colors cursor-pointer group"
                      onClick={() => handleSelectMinistry(m)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#C8A96E]/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-2-line text-[#C8A96E]"></i>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteMinistry(m.id); }}
                          className="opacity-0 group-hover:opacity-100 text-white/20 hover:text-red-400 transition-all p-1"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                      <h3
                        className="text-white font-semibold text-sm mb-1 truncate"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {m.name}
                      </h3>
                      <p className="text-white/30 text-xs">
                        {mTeams.length} {mTeams.length === 1 ? 'equipe' : 'equipes'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Ministry Detail View ───────────────────────────────────────────────────
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#0A0A0F] text-white flex flex-col pt-16">
        {/* Ministry header bar */}
        <div className="flex items-center gap-3 px-6 md:px-8 py-3 border-b border-white/5 bg-[#0D0C09]">
          <button
            onClick={() => { setView('ministry-list'); setActiveTeam(null); }}
            className="p-1.5 rounded-lg hover:bg-white/5 transition-colors text-white/50 hover:text-white"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-building-2-line text-[#C8A96E] text-sm flex-shrink-0"></i>
            <span
              className="text-white font-semibold text-sm truncate"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {activeMinistry?.name ?? 'Ministério'}
            </span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row flex-1 gap-0 min-w-0">
          {/* Sidebar: teams list */}
          <aside
            className={`w-full md:w-64 border-r border-white/5 bg-[#0D0C09] flex flex-col flex-shrink-0 ${
              activeTeam ? 'hidden md:flex' : 'flex'
            }`}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <span className="text-white/50 text-xs uppercase tracking-widest">Equipes</span>
              <button
                onClick={() => setShowCreateTeam(!showCreateTeam)}
                className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-[#C8A96E] transition-colors"
                title="Nova equipe"
              >
                <i className="ri-add-line text-sm"></i>
              </button>
            </div>

            {showCreateTeam && (
              <form onSubmit={handleCreateTeam} className="px-4 py-3 border-b border-white/5">
                <input
                  type="text"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="Nome da equipe"
                  autoFocus
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors mb-2"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowCreateTeam(false); setTeamName(''); }}
                    className="flex-1 py-1.5 text-white/40 hover:text-white text-xs transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !teamName.trim()}
                    className="flex-1 py-1.5 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-lg text-xs hover:bg-[#D4B87A] disabled:opacity-50 transition-colors"
                  >
                    {isSubmitting ? '...' : 'Criar'}
                  </button>
                </div>
              </form>
            )}

            {error && (
              <p className="px-5 py-2 text-red-400 text-xs">{error}</p>
            )}

            <nav className="flex-1 overflow-y-auto py-2">
              {ministryTeams.length === 0 ? (
                <div className="px-5 py-8 text-center">
                  <p className="text-white/20 text-xs">Nenhuma equipe</p>
                  <button
                    onClick={() => setShowCreateTeam(true)}
                    className="mt-3 text-[#C8A96E] text-xs hover:underline"
                  >
                    Criar equipe
                  </button>
                </div>
              ) : (
                ministryTeams.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => setActiveTeam(team.id)}
                    className={`w-full text-left flex items-center gap-3 px-5 py-3 text-sm transition-colors whitespace-nowrap ${
                      activeTeamId === team.id
                        ? 'text-[#C8A96E] bg-[#C8A96E]/5 border-r-2 border-[#C8A96E]'
                        : 'text-white/50 hover:text-white hover:bg-white/[0.03]'
                    }`}
                  >
                    <i className="ri-team-line text-sm flex-shrink-0"></i>
                    <span className="truncate">{team.name}</span>
                  </button>
                ))
              )}
            </nav>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
            {activeTeam ? (
              <TeamDetail
                team={activeTeam}
                onBack={() => setActiveTeam(null)}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-center px-8">
                <div>
                  <i className="ri-team-line text-white/10 text-5xl mb-4 block"></i>
                  <p className="text-white/30 text-sm">
                    {ministryTeams.length === 0
                      ? 'Crie uma equipe para começar'
                      : 'Selecione uma equipe no painel lateral'}
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
